<div class="vendor-page">
  <div class="vendor-shell p-3 p-lg-4">
    <div class="d-flex flex-column flex-xl-row justify-content-between gap-3 align-items-xl-center">
      <div class="d-flex align-items-center gap-3">
        <span class="title-badge">
          <lucide-icon [img]="UploadIcon" [size]="20"></lucide-icon>
        </span>
        <div>
          <h1 class="page-title-lite">Application Approval</h1>
          <p class="page-subtitle-lite mb-0">Punched applications with approval-file import, read status, and unread reset.</p>
        </div>
      </div>

      <div class="d-flex gap-2 flex-wrap justify-content-xl-end align-items-center">
        <a class="btn btn-sm create-btn export" routerLink="/applications">Back to Applications</a>
      </div>
    </div>
  </div>

  <div class="panel-card p-3 p-lg-4">
    <div class="toolbar-row mb-3">
      <div class="toolbar-filters approval-toolbar-filters">
        <div class="filter-field compact">
          <label class="form-label mb-1">Search By</label>
          <select class="form-select form-select-sm" [ngModel]="filters().searchBy" (ngModelChange)="updateFilter('searchBy', $event)">
            <option value="application">Application</option>
            <option value="contract">Contract</option>
          </select>
        </div>
        <div class="filter-field approval-search-field">
          <label class="form-label mb-1">{{ filters().searchBy === 'contract' ? 'Search Contract' : 'Search Application' }}</label>
          <input
            class="form-control form-control-sm search-input-lite"
            type="text"
            [placeholder]="filters().searchBy === 'contract' ? 'Contract number' : 'Application number'"
            [ngModel]="filters().searchText"
            (ngModelChange)="updateFilter('searchText', $event)"
          />
        </div>
        <div class="filter-field compact">
          <label class="form-label mb-1">Status</label>
          <select class="form-select form-select-sm" [ngModel]="filters().status" (ngModelChange)="updateFilter('status', $event)">
            <option value="all">All</option>
            <option value="approved">Approved</option>
            <option value="partial-approved">Partial Approved</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div class="filter-field compact">
          <label class="form-label mb-1">From Date</label>
          <input class="form-control form-control-sm" type="date" [ngModel]="filters().fromDate" (ngModelChange)="updateFilter('fromDate', $event)" />
        </div>
        <div class="filter-field compact">
          <label class="form-label mb-1">To Date</label>
          <input class="form-control form-control-sm" type="date" [ngModel]="filters().toDate" (ngModelChange)="updateFilter('toDate', $event)" />
        </div>
      </div>

      <div class="toolbar-filters">
        <button type="button" class="btn btn-sm create-btn export" (click)="clearFilters()">Reset</button>
      </div>
    </div>

    @if (loading()) {
      <div class="empty-state">Loading applications...</div>
    } @else {
      <div class="table-wrap">
        <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
          <table class="table align-middle mb-0">
            <thead class="table-head sticky-top">
              <tr>
                <th>S.No.</th>
                <th>Application No.</th>
                <th>Type</th>
                <th>Date</th>
                <th>Contract</th>
                <th>Requested Quantum</th>
                <th>Approved Quantum</th>
                <th>Deviation</th>
                <th>Status</th>
                <th>File Status</th>
                <th>Import File</th>
              </tr>
            </thead>
            <tbody>
              @if (filteredApplications().length === 0) {
                <tr>
                  <td colspan="11" class="text-center py-5 text-muted">No punched applications found</td>
                </tr>
              }
              @for (row of paginatedApplications(); track row.id; let i = $index) {
                <tr>
                  <td>{{ ((currentPage() - 1) * pageSize()) + i + 1 }}</td>
                  <td>{{ row.application_number }}</td>
                  <td>{{ applicationTypeLabel(row.application_type) }}</td>
                  <td>{{ formatDisplayDate(row.application_date) }}</td>
                  <td>{{ contractDisplay(row) }}</td>
                  <td>{{ formatQuantumMwh(requestedQuantum(row)) }}</td>
                  <td>
                    <button type="button" class="approved-quantum-btn" (click)="openApprovalDetails(row)">
                      {{ formatQuantumMwh(approvedQuantum(row)) }}
                    </button>
                  </td>
                  <td>
                    <div class="deviation-cell">
                      <span class="deviation-value" [class.positive]="deviationTone(row) === 'positive'" [class.negative]="deviationTone(row) === 'negative'">
                        {{ formatQuantum(deviationQuantum(row)) }}
                      </span>
                      <div class="deviation-track">
                        <div
                          class="deviation-bar"
                          [class.positive]="deviationTone(row) === 'positive'"
                          [class.negative]="deviationTone(row) === 'negative'"
                          [style.width.%]="deviationWidth(row)"
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      class="status-pill text-uppercase"
                      [class.approved]="approvalState(row) === 'approved'"
                      [class.partial]="approvalState(row) === 'partial-approved'"
                      [class.pending]="approvalState(row) === 'pending'"
                    >
                      {{ approvalStatusLabel(row) }}
                    </span>
                  </td>
                  <td>
                    <div class="file-status-stack">
                      @if (fileStatus(row.application_number) === 'File Not Found') {
                        <span class="status-pill missing">File Not Found</span>
                      }
                      @if (primaryApprovalFile(row.application_number); as file) {
                        <div class="approval-file-actions">
                          @if (file.error_message) {
                            <div class="error-text actions-only">{{ file.error_message }}</div>
                          }
                          <div class="actions">
                            <button
                              type="button"
                              class="action-btn neutral"
                              (click)="downloadApprovalFile(file)"
                              [title]="file.original_name"
                            >
                              <lucide-icon [img]="DownloadIcon" [size]="13"></lucide-icon>
                            </button>
                            <button
                              type="button"
                              class="action-btn neutral"
                              [disabled]="!file.is_read"
                              (click)="markAsUnread(file)"
                              title="Mark unread"
                            >
                              <lucide-icon [img]="RotateCcwIcon" [size]="13"></lucide-icon>
                            </button>
                          </div>
                        </div>
                      }
                    </div>
                  </td>
                  <td>
                    <input #fileInput class="d-none" type="file" accept=".pdf,.txt,application/pdf,text/plain" (change)="onImportFile(row, $event)" />
                    <button type="button" class="btn btn-sm create-btn primary import-btn" [disabled]="isUploading(row.id)" (click)="openFilePicker(fileInput)">
                      <lucide-icon [img]="UploadIcon" [size]="14"></lucide-icon>
                      <span class="ms-1">{{ isUploading(row.id) ? 'Importing...' : 'Import File' }}</span>
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
      @if (filteredApplications().length > 0) {
        <div class="pager-row mt-3">
          <div class="pager-meta">
            {{ pageStart() }}-{{ pageEnd() }} of {{ filteredApplications().length }} punched applications
          </div>
          <div class="pagination-shell">
            <button class="pager-btn" [disabled]="currentPage() <= 1" (click)="goFirst()" aria-label="First page">
              <lucide-icon [img]="ChevronsLeftIcon" [size]="14"></lucide-icon>
            </button>
            <button class="pager-btn" [disabled]="currentPage() <= 1" (click)="goToPage(currentPage() - 1)" aria-label="Previous page">
              <lucide-icon [img]="ChevronLeftIcon" [size]="14"></lucide-icon>
            </button>
            <span class="page-indicator current">{{ currentPage() }}</span>
            <button class="pager-btn" [disabled]="currentPage() >= totalPages()" (click)="goToPage(currentPage() + 1)" aria-label="Next page">
              <lucide-icon [img]="ChevronRightIcon" [size]="14"></lucide-icon>
            </button>
            <button class="pager-btn" [disabled]="currentPage() >= totalPages()" (click)="goLast()" aria-label="Last page">
              <lucide-icon [img]="ChevronsRightIcon" [size]="14"></lucide-icon>
            </button>
            <select class="form-select form-select-sm page-size-select pager-size-select" [ngModel]="pageSize()" (ngModelChange)="onPageSizeChange(($event || '10').toString())" aria-label="Rows per page">
              @for (size of pageSizes; track size) {
                <option [ngValue]="size">{{ size }}/Page</option>
              }
            </select>
          </div>
        </div>
      }
    }
  </div>
</div>

@if (selectedApprovalDetails(); as details) {
  <div class="modal-backdrop fade show" (click)="closeApprovalDetails()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content approval-details-modal" (click)="$event.stopPropagation()">
        <div class="modal-header approval-details-head">
          <div>
            <h5 class="modal-title mb-1">Approved Quantum Details</h5>
            <div class="approval-details-subtitle">
              {{ details.application_number }} | {{ contractDisplay(details) }}
            </div>
          </div>
          <button type="button" class="btn-close" aria-label="Close" (click)="closeApprovalDetails()"></button>
        </div>
        <div class="modal-body approval-details-body">
          <div class="approval-summary-grid">
            <div class="approval-summary-card">
              <span class="approval-summary-label">Approved Quantum</span>
              <strong>{{ formatQuantum(approvedQuantum(details)) }} MWh</strong>
            </div>
            <div class="approval-summary-card">
              <span class="approval-summary-label">Requested Quantum</span>
              <strong>{{ formatQuantum(requestedQuantum(details)) }} MWh</strong>
            </div>
            <div class="approval-summary-card">
              <span class="approval-summary-label">Status</span>
              <strong>{{ approvalStatusLabel(details) }}</strong>
            </div>
          </div>

          <div class="charge-table-wrap">
            <div class="charge-table-head">
              <h6 class="mb-0">Charges Applied</h6>
              <span class="text-muted small">Stored approval charge rows from database</span>
            </div>

            @if (approvalChargeRows(details).length) {
              <div class="table-responsive">
                <table class="table table-sm align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>S.No.</th>
                      <th>Charge Type</th>
                      <th>Group</th>
                      <th>Section</th>
                      <th>Rate/MWh</th>
                      <th>Quantum</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (charge of approvalChargeRows(details); track charge.group_name + '-' + charge.sub_group_name + '-' + $index; let i = $index) {
                      <tr>
                        <td>{{ i + 1 }}</td>
                        <td>{{ approvalChargeType(charge, i) }}</td>
                        <td>{{ charge.group_name || '-' }}</td>
                        <td>{{ charge.section_heading || '-' }}</td>
                        <td>{{ formatQuantum(charge.rate_mwh ?? null) }}</td>
                        <td>{{ formatQuantum(charge.quantum_mwh ?? null) }}</td>
                        <td>{{ formatQuantum(approvalChargeAmount(charge)) }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <div class="empty-charge-state">No approval charge rows are stored for this application.</div>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
}
